/**
 * SSO callback: exchanges the hosted-UI ?code= for tokens and enters the app.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { completeSsoLogin } from '../lib/sso';

export function Callback(): React.ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = params.get('code');
    const idpError = params.get('error_description') ?? params.get('error');
    if (idpError) {
      setError(idpError);
      return;
    }
    if (!code) {
      setError('No sign-in code present. Start again from the login page.');
      return;
    }
    completeSsoLogin(code)
      .then((idToken) => {
        loginWithToken(idToken);
        navigate('/', { replace: true });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Sign-in failed.');
      });
  }, [params, loginWithToken, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900">
      <div className="rounded-lg bg-white p-8 shadow-md">
        {error ? (
          <>
            <p className="mb-2 font-semibold text-red-600">Sign-in failed</p>
            <p className="mb-4 text-sm text-slate-700">{error}</p>
            <a className="text-sm font-medium text-sky-600 underline" href="/login">
              Back to sign-in
            </a>
          </>
        ) : (
          <p className="text-slate-700">Completing sign-in…</p>
        )}
      </div>
    </div>
  );
}
